import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { skillsApi } from "./api";
import type { SkillCatalog } from "../types/api";

export type SkillSelectGroup = { group: string; items: { value: string; label: string }[] };

function catalogToGrouped(catalog: SkillCatalog | undefined): SkillSelectGroup[] {
  if (!catalog?.groups?.length) return [];
  return catalog.groups.map((g) => ({
    group: g.category_label,
    items: g.skills.map((s) => ({ value: s.code, label: s.label })),
  }));
}

function catalogToFlat(catalog: SkillCatalog | undefined): { value: string; label: string }[] {
  if (!catalog?.options?.length) return [];
  return catalog.options.map((s) => ({ value: s.code, label: s.label }));
}

function catalogToSuggestionLabels(catalog: SkillCatalog | undefined): string[] {
  if (!catalog?.options?.length) return [];
  return catalog.options.map((s) => s.label);
}

function catalogToCodeByLabel(catalog: SkillCatalog | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const opt of catalog?.options ?? []) {
    map.set(opt.label.toLowerCase(), opt.code);
    map.set(opt.code.toLowerCase(), opt.code);
  }
  return map;
}

export function useSkillCatalog(enabled = true) {
  const query = useQuery({
    queryKey: ["skill-catalog"],
    queryFn: () => skillsApi.catalog(),
    enabled,
    staleTime: 5 * 60_000,
  });
  const groupedOptions = useMemo(() => catalogToGrouped(query.data), [query.data]);
  const flatOptions = useMemo(() => catalogToFlat(query.data), [query.data]);
  const suggestionLabels = useMemo(() => catalogToSuggestionLabels(query.data), [query.data]);
  const labels = query.data?.labels;
  const languageSuggestionLabels = useMemo(() => {
    const group = query.data?.groups?.find((g) => g.category === "language_skill");
    return group?.skills.map((s) => s.label) ?? [];
  }, [query.data]);
  const codeByLabel = useMemo(() => catalogToCodeByLabel(query.data), [query.data]);
  return { ...query, groupedOptions, flatOptions, suggestionLabels, labels, codeByLabel, languageSuggestionLabels };
}

export function usePublicSkillCatalog(enabled = true) {
  const query = useQuery({
    queryKey: ["public-skill-catalog"],
    queryFn: () => skillsApi.publicCatalog(),
    enabled,
    staleTime: 5 * 60_000,
  });
  const groupedOptions = useMemo(() => catalogToGrouped(query.data), [query.data]);
  const flatOptions = useMemo(() => catalogToFlat(query.data), [query.data]);
  const suggestionLabels = useMemo(() => catalogToSuggestionLabels(query.data), [query.data]);
  const labels = query.data?.labels;
  const languageSuggestionLabels = useMemo(() => {
    const group = query.data?.groups?.find((g) => g.category === "language_skill");
    return group?.skills.map((s) => s.label) ?? [];
  }, [query.data]);
  const codeByLabel = useMemo(() => catalogToCodeByLabel(query.data), [query.data]);
  return { ...query, groupedOptions, flatOptions, suggestionLabels, labels, codeByLabel, languageSuggestionLabels };
}
